import { useState, useEffect, useCallback } from "react";
import { getApiUrl } from "../../../../config/api";
import { useAuth } from "../../../../firebase/AuthContext";

const BOT_API = `${getApiUrl()}/api/bot-dashboard`;
const authHeader = (token) => ({ Authorization: `Bearer ${token}` });

const Spinner = () => (
  <div className="flex justify-center py-16">
    <svg
      className="animate-spin h-8 w-8 text-[#E78A24]"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  </div>
);

const LeadsTab = ({ bot, isActive, onUnreadCountChange, token }) => {
  const { hasRole } = useAuth();
  // Reading needs webchatbot.leads.read (the tab only shows with it); every
  // change - read flags, trash, delete, reply, forwarding - needs .manage.
  const canManage = hasRole("webchatbot.leads.manage");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replying, setReplying] = useState(false);
  const [replyStatus, setReplyStatus] = useState(null);
  const [trashMode, setTrashMode] = useState(false);
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [forwardEmails, setForwardEmails] = useState([]);
  const [forwardInput, setForwardInput] = useState("");
  const [savingForward, setSavingForward] = useState(false);
  const [forwardSaveStatus, setForwardSaveStatus] = useState(null);

  useEffect(() => {
    const raw = bot?.forward_email || "";
    const list = raw
      .split(",")
      .map((e) => e.trim())
      .filter(Boolean);
    setForwardEmails(list);
  }, [bot?.forward_email]);

  const loadLeads = useCallback(async () => {
    try {
      const res = await fetch(`${BOT_API}/${bot.bot_id}/leads`, {
        headers: authHeader(token),
      });
      const data = await res.json();
      if (res.ok && data.success) setLeads(data.leads || []);
    } catch (err) {
      console.error("[LeadsTab] Failed to load contact requests:", err);
    } finally {
      setLoading(false);
    }
  }, [bot?.bot_id, token]);

  // Contacts are read through the backend so the webchatbot.leads.* roles
  // apply; polling stands in for the live database listener this used to use.
  useEffect(() => {
    if (!isActive || !bot?.bot_id) return;
    setLoading(true);
    loadLeads();
    const timer = setInterval(loadLeads, 30000);
    return () => clearInterval(timer);
  }, [isActive, bot?.bot_id, loadLeads]);

  useEffect(() => {
    onUnreadCountChange?.(leads.filter((l) => !l.read).length);
  }, [leads]); // eslint-disable-line react-hooks/exhaustive-deps

  // Applied on screen straight away; reloaded from the server if any write fails.
  const patchLeads = (ids, patch) => {
    setLeads((prev) =>
      prev.map((l) => (ids.includes(l.id) ? { ...l, ...patch } : l)),
    );
    return Promise.all(
      ids.map((id) =>
        fetch(`${BOT_API}/${bot.bot_id}/leads/${id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", ...authHeader(token) },
          body: JSON.stringify(patch),
        }),
      ),
    )
      .then((responses) => {
        if (responses.some((r) => !r.ok)) loadLeads();
      })
      .catch(() => loadLeads());
  };

  const removeLeads = async (ids) => {
    await Promise.all(
      ids.map((id) =>
        fetch(`${BOT_API}/${bot.bot_id}/leads/${id}`, {
          method: "DELETE",
          headers: authHeader(token),
        }),
      ),
    );
    await loadLeads();
  };

  const checkedIn = (list) =>
    [...checkedIds].filter((id) => list.find((l) => l.id === id));

  const addForwardEmail = () => {
    const email = forwardInput.trim();
    if (!email || !email.includes("@")) return;
    if (forwardEmails.includes(email)) {
      setForwardInput("");
      return;
    }
    setForwardEmails([...forwardEmails, email]);
    setForwardInput("");
    setForwardSaveStatus(null);
  };

  const removeForwardEmail = (email) => {
    setForwardEmails(forwardEmails.filter((e) => e !== email));
    setForwardSaveStatus(null);
  };

  const saveForwardEmails = async () => {
    const pending = forwardInput.trim();
    const finalList =
      pending && pending.includes("@") && !forwardEmails.includes(pending)
        ? [...forwardEmails, pending]
        : forwardEmails;
    if (pending) {
      setForwardEmails(finalList);
      setForwardInput("");
    }

    setSavingForward(true);
    setForwardSaveStatus(null);
    try {
      const res = await fetch(`${BOT_API}/${bot.bot_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeader(token) },
        body: JSON.stringify({ forward_email: finalList.join(",") }),
      });
      if (!res.ok) throw new Error();
      setForwardSaveStatus("saved");
    } catch {
      setForwardSaveStatus("error");
    } finally {
      setSavingForward(false);
    }
  };

  const handleSelect = (lead) => {
    setSelectedId(lead.id);
    setReplyText("");
    setReplyStatus(null);
    if (!lead.read && canManage) patchLeads([lead.id], { read: true });
  };

  const handleReply = async () => {
    if (!replyText.trim() || !selected) return;
    setReplying(true);
    setReplyStatus(null);
    try {
      await fetch(`${BOT_API}/${bot.bot_id}/leads/${selected.id}/reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader(token) },
        body: JSON.stringify({ reply_message: replyText.trim() }),
      }).then((r) => {
        if (!r.ok) throw new Error("Failed");
      });
      setReplyStatus("sent");
      setReplyText("");
    } catch {
      setReplyStatus("error");
    } finally {
      setReplying(false);
    }
  };

  const handleDelete = (lead) => {
    setDeletingId(lead.id);
    patchLeads([lead.id], { deleted: true })
      .then(() => {
        if (selectedId === lead.id) setSelectedId(null);
      })
      .finally(() => setDeletingId(null));
  };

  const handleRestore = (lead) => {
    patchLeads([lead.id], { deleted: false });
    if (selectedId === lead.id) setSelectedId(null);
  };

  const handlePermanentDelete = async (lead) => {
    if (
      !window.confirm(
        `Permanently delete this request from ${lead.email}? This cannot be undone.`,
      )
    )
      return;
    setDeletingId(lead.id);
    try {
      await removeLeads([lead.id]);
      if (selectedId === lead.id) setSelectedId(null);
    } finally {
      setDeletingId(null);
    }
  };

  const handleMarkUnread = (lead) => {
    patchLeads([lead.id], { read: false });
  };

  const toggleCheck = (id, e) => {
    e.stopPropagation();
    setCheckedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleCheckAll = (list) => {
    if (checkedIds.size === list.length) {
      setCheckedIds(new Set());
    } else {
      setCheckedIds(new Set(list.map((l) => l.id)));
    }
  };

  const batchMarkRead = (list) => {
    patchLeads(checkedIn(list), { read: true });
    setCheckedIds(new Set());
  };

  const batchMarkUnread = (list) => {
    patchLeads(checkedIn(list), { read: false });
    setCheckedIds(new Set());
  };

  const batchMoveToTrash = (list) => {
    patchLeads(checkedIn(list), { deleted: true });
    setCheckedIds(new Set());
    if (checkedIds.has(selectedId)) setSelectedId(null);
  };

  const batchRestore = (list) => {
    patchLeads(checkedIn(list), { deleted: false });
    setCheckedIds(new Set());
    if (checkedIds.has(selectedId)) setSelectedId(null);
  };

  const batchPermanentDelete = async (list) => {
    if (
      !window.confirm(
        `Permanently delete ${checkedIds.size} item(s)? This cannot be undone.`,
      )
    )
      return;
    const ids = checkedIn(list);
    setCheckedIds(new Set());
    await removeLeads(ids);
    if (ids.includes(selectedId)) setSelectedId(null);
  };

  const activeLeads = leads.filter((l) => !l.deleted);
  const trashedLeads = leads.filter((l) => l.deleted);
  const displayList = trashMode ? trashedLeads : activeLeads;
  const selected = displayList.find((l) => l.id === selectedId) || null;
  const unreadCount = activeLeads.filter((l) => !l.read).length;

  if (loading) return <Spinner />;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 mb-4">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold text-gray-800">
            {trashMode ? "Trash" : "Contact Requests"}
          </h2>
          {!trashMode && unreadCount > 0 && (
            <span className="bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5">
              {unreadCount} unread
            </span>
          )}
        </div>
        <span className="text-xs text-gray-400">
          {trashMode
            ? `${trashedLeads.length} in trash`
            : `${activeLeads.length} total`}
        </span>
      </div>

      {/* Forward email settings */}
      {canManage && (
      <div className="mx-6 mb-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
        <p className="text-xs font-medium text-gray-600 mb-2">
          Auto-forward new requests to:
        </p>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {forwardEmails.map((email) => (
            <span
              key={email}
              className="inline-flex items-center gap-1 bg-white border border-gray-300 rounded-full px-2.5 py-0.5 text-xs text-gray-700"
            >
              {email}
              <button
                onClick={() => removeForwardEmail(email)}
                className="text-gray-400 hover:text-red-400 ml-0.5 leading-none"
              >
                ×
              </button>
            </span>
          ))}
          {forwardEmails.length === 0 && (
            <span className="text-xs text-gray-400 italic">
              No forwarding set
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <input
            type="email"
            value={forwardInput}
            onChange={(e) => setForwardInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addForwardEmail()}
            placeholder="Add email and press Enter"
            className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white text-gray-800 focus:outline-none focus:ring-1 focus:ring-[#E78A24]"
          />
          <button
            onClick={addForwardEmail}
            className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-white transition-colors"
          >
            Add
          </button>
          <button
            onClick={saveForwardEmails}
            disabled={savingForward}
            className="text-sm px-3 py-1.5 rounded-lg bg-[#E78A24] text-white font-medium hover:bg-[#d17820] disabled:opacity-40 transition-colors"
          >
            {savingForward ? "Saving…" : "Save"}
          </button>
        </div>
        {forwardSaveStatus === "saved" && (
          <p className="text-xs text-green-500 mt-1.5">Saved.</p>
        )}
        {forwardSaveStatus === "error" && (
          <p className="text-xs text-red-400 mt-1.5">
            Failed to save. Please try again.
          </p>
        )}
      </div>
      )}

      {/* Trash toggle */}
      <div className="mx-6 mb-3 flex items-center gap-2">
        {trashMode ? (
          <button
            onClick={() => {
              setTrashMode(false);
              setSelectedId(null);
              setCheckedIds(new Set());
            }}
            className="flex items-center gap-1.5 text-xs text-[#E78A24] hover:underline"
          >
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Contact Requests
          </button>
        ) : (
          <button
            onClick={() => {
              setTrashMode(true);
              setSelectedId(null);
              setCheckedIds(new Set());
            }}
            className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
            Trash
            {trashedLeads.length > 0 && (
              <span className="bg-gray-200 text-gray-500 text-[10px] rounded-full px-1.5 py-0.5">
                {trashedLeads.length}
              </span>
            )}
          </button>
        )}
      </div>

      {displayList.length === 0 && checkedIds.size === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <svg
            className="w-12 h-12 mx-auto mb-3 opacity-40"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d={
                trashMode
                  ? "M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  : "M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              }
            />
          </svg>
          <p className="text-sm">
            {trashMode ? "Trash is empty" : "No contact requests yet"}
          </p>
          {!trashMode && (
            <p className="text-xs mt-1">
              Visitors who contact you via the widget will appear here.
            </p>
          )}
          {trashMode && (
            <button
              onClick={() => {
                setTrashMode(false);
                setSelectedId(null);
                setCheckedIds(new Set());
              }}
              className="mt-3 text-xs text-[#E78A24] hover:underline"
            >
              Back to Contact Requests
            </button>
          )}
        </div>
      ) : (
        <div
          className="flex gap-0 border border-gray-200 rounded-lg overflow-hidden mx-6"
          style={{ minHeight: 420 }}
        >
          {/* Left list */}
          <div className="w-72 shrink-0 border-r border-gray-200 flex flex-col bg-gray-50 overflow-hidden">
            {/* Batch action bar */}
            {canManage && (
            <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200 bg-gray-100 shrink-0">
              <input
                type="checkbox"
                checked={
                  checkedIds.size > 0 && checkedIds.size === displayList.length
                }
                ref={(el) => {
                  if (el)
                    el.indeterminate =
                      checkedIds.size > 0 &&
                      checkedIds.size < displayList.length;
                }}
                onChange={() => toggleCheckAll(displayList)}
                className="accent-[#E78A24] cursor-pointer"
              />
              {checkedIds.size > 0 ? (
                <div className="flex items-center gap-1 flex-1 flex-wrap">
                  <span className="text-xs text-gray-500 mr-1">
                    {checkedIds.size} selected
                  </span>
                  {!trashMode && (
                    <>
                      <button
                        onClick={() => batchMarkRead(displayList)}
                        className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-gray-300 text-gray-600 hover:bg-orange-50"
                      >
                        Read
                      </button>
                      <button
                        onClick={() => batchMarkUnread(displayList)}
                        className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-gray-300 text-gray-600 hover:bg-orange-50"
                      >
                        Unread
                      </button>
                      <button
                        onClick={() => batchMoveToTrash(displayList)}
                        className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-gray-300 text-red-400 hover:bg-red-50"
                      >
                        Trash
                      </button>
                    </>
                  )}
                  {trashMode && (
                    <>
                      <button
                        onClick={() => batchRestore(displayList)}
                        className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-gray-300 text-gray-600 hover:bg-orange-50"
                      >
                        Restore
                      </button>
                      <button
                        onClick={() => batchPermanentDelete(displayList)}
                        className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-gray-300 text-red-400 hover:bg-red-50"
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <span className="text-xs text-gray-400">Select all</span>
              )}
            </div>
            )}

            <div className="overflow-y-auto flex-1">
              {displayList.map((lead) => {
                const isUnread = !lead.read && !trashMode;
                const isSelected = lead.id === selectedId;
                const isChecked = checkedIds.has(lead.id);
                const preview = lead.message
                  ? lead.message.slice(0, 50) +
                    (lead.message.length > 50 ? "…" : "")
                  : "No message";
                const time = lead.submitted_at
                  ? new Date(lead.submitted_at).toLocaleString([], {
                      month: "numeric",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : "";
                return (
                  <div
                    key={lead.id}
                    onClick={() => handleSelect(lead)}
                    className={`flex items-start gap-2 px-3 py-3 border-b border-gray-200 cursor-pointer transition-colors ${
                      isSelected
                        ? "bg-orange-50 border-l-2 border-l-[#E78A24]"
                        : "hover:bg-white"
                    }`}
                  >
                    {canManage && (
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => toggleCheck(lead.id, e)}
                        onClick={(e) => e.stopPropagation()}
                        className="accent-[#E78A24] cursor-pointer mt-1 shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        {isUnread && (
                          <span className="w-2 h-2 rounded-full bg-[#E78A24] shrink-0" />
                        )}
                        <span
                          className={`text-sm truncate ${isUnread ? "font-semibold text-gray-900" : "font-normal text-gray-600"}`}
                        >
                          {lead.email}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs text-gray-400 truncate">
                          {preview}
                        </span>
                        <span className="text-[10px] text-gray-400 shrink-0">
                          {time}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right detail panel */}
          <div className="flex-1 bg-white overflow-y-auto">
            {selected ? (
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-base font-semibold text-gray-900">
                      {selected.email}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {selected.submitted_at
                        ? new Date(selected.submitted_at).toLocaleString()
                        : ""}
                    </p>
                  </div>
                  {canManage && (
                  <div className="flex items-center gap-2">
                    {trashMode ? (
                      <>
                        <button
                          onClick={() => handleRestore(selected)}
                          className="text-xs text-gray-500 hover:text-[#E78A24] px-2 py-1 rounded hover:bg-gray-100 transition-colors"
                        >
                          Restore
                        </button>
                        <button
                          onClick={() => handlePermanentDelete(selected)}
                          disabled={deletingId === selected.id}
                          className="text-xs text-red-400 hover:text-red-600 px-2 py-1 rounded hover:bg-red-50 transition-colors"
                        >
                          {deletingId === selected.id
                            ? "Deleting…"
                            : "Delete permanently"}
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleMarkUnread(selected)}
                          className="text-xs text-gray-500 hover:text-[#E78A24] px-2 py-1 rounded hover:bg-gray-100 transition-colors"
                        >
                          Mark unread
                        </button>
                        <button
                          onClick={() => handleDelete(selected)}
                          disabled={deletingId === selected.id}
                          className="text-xs text-red-400 hover:text-red-600 px-2 py-1 rounded hover:bg-red-50 transition-colors"
                        >
                          {deletingId === selected.id
                            ? "Moving…"
                            : "Move to Trash"}
                        </button>
                      </>
                    )}
                  </div>
                  )}
                </div>

                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {selected.message || (
                      <span className="italic text-gray-400">
                        No message provided.
                      </span>
                    )}
                  </p>
                </div>

                {selected.session_id && (
                  <div className="text-xs text-gray-400 font-mono mb-5">
                    Session: {selected.session_id}
                  </div>
                )}

                {!trashMode && canManage && (
                  <div className="border-t border-gray-200 pt-4">
                    <p className="text-xs font-medium text-gray-500 mb-2">
                      Reply to {selected.email}
                    </p>
                    <textarea
                      value={replyText}
                      onChange={(e) => {
                        setReplyText(e.target.value);
                        setReplyStatus(null);
                      }}
                      rows={3}
                      placeholder="Write your reply..."
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white text-gray-800 resize-none focus:outline-none focus:ring-1 focus:ring-[#E78A24]"
                    />
                    <div className="flex items-center justify-between mt-2">
                      <div className="text-xs">
                        {replyStatus === "sent" && (
                          <span className="text-green-500">
                            Sent successfully.
                          </span>
                        )}
                        {replyStatus === "error" && (
                          <span className="text-red-400">
                            Failed to send. Please try again.
                          </span>
                        )}
                      </div>
                      <button
                        onClick={handleReply}
                        disabled={replying || !replyText.trim()}
                        className="text-sm px-4 py-1.5 rounded-lg bg-[#E78A24] text-white font-medium hover:bg-[#d17820] disabled:opacity-40 transition-colors"
                      >
                        {replying ? "Sending…" : "Send Reply"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 py-16">
                <svg
                  className="w-10 h-10 mb-3 opacity-30"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                <p className="text-sm">
                  {trashMode
                    ? "Select an item to view"
                    : "Select a request to view details"}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsTab;
